import { React, useState, useEffect, useRef, Fragment } from 'react'
import { motion } from "framer-motion";
import { Helmet } from 'react-helmet'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faEnvelopesBulk, faCircleInfo, faClockRotateLeft, faNewspaper, faUsers, faTriangleExclamation, faFlask, faBold, faItalic, faEye, faBell, faEnvelope } from '@fortawesome/free-solid-svg-icons'

import MainHeader from '../../../components/MainHeader/MainHeader.js'
import MainFooter from '../../../components/MainFooter/MainFooter.js'
import CTAButton from '../../../components/CTAButton/CTAButton.js'
import Skeleton from '../../../components/Skeleton/Skeleton.js'
import AdminGuard from '../../../components/AdminGuard/AdminGuard.js'

import { boxvar2_1, boxvar2_2, boxvar4, URL_MAINAPI, API_TOKEN, formatDateWithTime } from '../../../main.js'

import '../../../main.css'
import './AdminMailing.css'
import './MAdminMailing.css'

import Cookies from 'universal-cookie'

// Must match INLINE_FORMAT_RE in helferzeit_mainapi mail/layout.js (Mail-Rendering)
const INLINE_FORMAT_RE = /(^|[^\p{L}\p{N}*])(?:\*\*(\S(?:.*?\S)??)\*\*|\*([^\s*](?:.*?[^\s*])??)\*)(?![\p{L}\p{N}*])/u
const MESSAGE_MAX_LENGTH = 20000

function renderInlineFormatting(line) {
    const nodes = []
    const re = new RegExp(INLINE_FORMAT_RE.source, 'gu')
    let last = 0
    let match
    while ((match = re.exec(line)) !== null) {
        const start = match.index + match[1].length
        if (start > last) nodes.push(line.slice(last, start))
        const isBold = match[2] !== undefined
        const inner = renderInlineFormatting(isBold ? match[2] : match[3])
        nodes.push(isBold ? <strong key={start}>{inner}</strong> : <em key={start}>{inner}</em>)
        last = re.lastIndex
    }
    if (last < line.length) nodes.push(line.slice(last))
    return nodes
}

function renderFormattedText(text) {
    return String(text || '').split(/\r\n|\r|\n/).map((line, index) => (
        <Fragment key={index}>{index > 0 ? '\n' : null}{renderInlineFormatting(line)}</Fragment>
    ))
}

function greetingPreview(firstname, lastname) {
    const name = [firstname, lastname].filter(Boolean).join(' ').trim()
    return name ? `Hallo ${name},` : 'Hallo Vorname Nachname,'
}

export default function AdminMailing() {

    var [subject, setSubject] = useState("")
    var [message, setMessage] = useState("")
    var [newsletterOnly, setNewsletterOnly] = useState(true)
    var [profileOnly, setProfileOnly] = useState(false)
    var [testOnly, setTestOnly] = useState(true)
    var [confirmed, setConfirmed] = useState(false)
    var [sending, setSending] = useState(false)
    var [banner, setBanner] = useState(null)

    var [counts, setCounts] = useState(null)
    var [history, setHistory] = useState([])
    var [loadedHistory, setLoadedHistory] = useState(0)
    var [expandedId, setExpandedId] = useState(null)
    const messageRef = useRef(null)

    const cookies = new Cookies()
    let cur_authtoken = cookies.get('authtoken')
    let cur_firstname = cookies.get('firstname')
    let cur_lastname = cookies.get('lastname')

    const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'APITOKEN': API_TOKEN,
        'authtoken': cur_authtoken
    }

    useEffect(() => {
        getCounts()
        getHistory()
    }, [])

    function getCounts() {
        try {
            fetch(URL_MAINAPI + "/mailing/platform/recipients", { method: "GET", headers: headers })
                .then(response => response.json().then(data => ({ data: data, status: response.status })).then(res => {
                    setCounts(res.status === 200 ? res.data.data : null)
                }))
        } catch (err) {
            setCounts(null)
        }
    }

    function getHistory() {
        try {
            setLoadedHistory(0)
            fetch(URL_MAINAPI + "/mailing/platform/history", { method: "GET", headers: headers })
                .then(response => response.json().then(data => ({ data: data, status: response.status })).then(res => {
                    setHistory((res.status === 200 && Array.isArray(res.data.data)) ? res.data.data : [])
                    setLoadedHistory(1)
                }))
        } catch (err) {
            setHistory([])
            setLoadedHistory(1)
        }
    }

    const recipientCount = counts ? (newsletterOnly ? counts.newsletter : counts.all) : null

    function wrapSelection(marker) {
        const el = messageRef.current
        if (!el) return
        let start = el.selectionStart
        let end = el.selectionEnd
        while (start < end && /\s/.test(message[start])) start++
        while (end > start && /\s/.test(message[end - 1])) end--

        const selected = message.slice(start, end) || (marker === '**' ? 'fetter Text' : 'kursiver Text')
        const multiline = selected.includes('\n')
        // Formatierung gilt nur innerhalb einer Zeile – daher jede Zeile einzeln umschließen
        const wrapped = multiline
            ? selected.split('\n').map((line) => (line.trim() ? marker + line.trim() + marker : line)).join('\n')
            : marker + selected + marker
        const next = message.slice(0, start) + wrapped + message.slice(end)
        if (next.length > MESSAGE_MAX_LENGTH) return

        setMessage(next)
        requestAnimationFrame(() => {
            el.focus()
            if (multiline) {
                el.setSelectionRange(start, start + wrapped.length)
            } else {
                el.setSelectionRange(start + marker.length, start + marker.length + selected.length)
            }
        })
    }

    function onMessageKeyDown(e) {
        if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return
        const key = e.key.toLowerCase()
        if (key === 'b') {
            e.preventDefault()
            wrapSelection('**')
        } else if (key === 'i') {
            e.preventDefault()
            wrapSelection('*')
        }
    }

    function submitLabel() {
        if (profileOnly && testOnly) return "Test-Benachrichtigung an mich"
        if (profileOnly) return "Nur im Profil veröffentlichen"
        if (testOnly) return "Testmail an mich senden"
        return "Rundmail verschicken"
    }

    function sendMails() {
        if (sending) return
        setBanner(null)

        if (subject.trim().length < 3 || message.trim().length < 10) {
            setBanner({ type: "error", text: "Bitte gib einen Betreff (mind. 3 Zeichen) und einen Nachrichtentext (mind. 10 Zeichen) ein." })
            return
        }
        if (!testOnly) {
            if (!confirmed) {
                setBanner({ type: "error", text: profileOnly ? "Du musst die Veröffentlichung bestätigen!" : "Du musst den Versand bestätigen!" })
                return
            }
            const target = newsletterOnly ? "alle Newsletter-Abonnenten" : "ALLE aktiven Helferzeit-User"
            const action = profileOnly
                ? ("Benachrichtigung wirklich für " + target + (recipientCount != null ? " (" + recipientCount + ")" : "") + " nur im Profil veröffentlichen?")
                : ("Rundmail wirklich an " + target + (recipientCount != null ? " (" + recipientCount + " Empfänger)" : "") + " senden?")
            if (!window.confirm(action)) return
        }

        setSending(true)
        setBanner({ type: "loading", text: "Lädt... Bitte warten..." })

        try {
            fetch(URL_MAINAPI + "/mailing/platform/send", {
                method: "POST",
                headers: headers,
                body: JSON.stringify({
                    subject: subject,
                    text: message,
                    newsletter: newsletterOnly,
                    testonly: testOnly,
                    profileonly: profileOnly,
                }),
            }).then(response => response.json().then(data => ({ data: data, status: response.status })).then(res => {
                setSending(false)
                setConfirmed(false)
                if (res.status === 200) {
                    let doneText
                    if (profileOnly) {
                        doneText = testOnly
                            ? "Die Test-Benachrichtigung wurde in deinem Profil hinterlegt (keine E-Mail)."
                            : "Die Benachrichtigung wurde für " + res.data.data.recipientcount + " User nur im Profil hinterlegt (keine E-Mails)."
                    } else if (testOnly) {
                        doneText = "Die Testmail an dich wurde in die Warteschleife gesetzt."
                    } else {
                        doneText = "Die E-Mails wurden in die Warteschleife gesetzt. Es werden " + res.data.data.recipientcount + " E-Mails nach und nach verschickt. Es kann teilweise 1h+ dauern bei großen Mengen."
                    }
                    setBanner({ type: "done", text: doneText, runid: res.data.data.runid })
                    getHistory()
                } else {
                    setBanner({ type: "error", text: "Es gab einen Fehler bei der Verarbeitung. Fehler: " + (res.data.error || "unbekannter Fehler") })
                }
            }))
        } catch (err) {
            setSending(false)
            setBanner({ type: "error", text: "Es gab einen Fehler bei der Verarbeitung. Fehler: " + err })
        }
    }

    return (
        <div className='mainCon page' id='mainCon'>
            <MainHeader selectedsite={""} />
            <Helmet>
                <meta charSet="utf-8" />
                <title>Plattform-Rundmail</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <main className='page-body container section' style={{ paddingTop: '36px' }}>
                <div className='breadcrumb'>
                    <a href='/profil'>Profil</a>
                    <FontAwesomeIcon icon={faChevronRight} />
                    <a href='/admin'>Helferzeit-Administration</a>
                    <FontAwesomeIcon icon={faChevronRight} />
                    <span>Plattform-Rundmail</span>
                </div>

                <motion.h1
                    variants={boxvar4}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: "-50px" }}
                    className='section-title'
                    style={{ marginBottom: '6px' }}
                >
                    📬 Plattform-Rundmail
                </motion.h1>
                <p className='section-sub'>Newsletter oder Information an User der Plattform – per E-Mail und/oder nur im Profil.</p>

                <AdminGuard>
                    <motion.div variants={boxvar2_1} initial="hidden" whileInView="visible" viewport={{ once: false, margin: "-50px" }} style={{ marginTop: '20px' }}>
                        <div className='card adminmail-card'>
                            <div className='adminmail-headline'>
                                <span className='adminmail-icon'><FontAwesomeIcon icon={faEnvelopesBulk} /></span>
                                <h3>Neue Plattformnachricht</h3>
                            </div>

                            <div className='field'>
                                <label>Art der Nachricht</label>
                                <div className='adminmail-chips' role="radiogroup" aria-label="Art der Nachricht">
                                    <button type="button" role="radio" aria-checked={newsletterOnly} className={'toggle-chip' + (newsletterOnly ? ' toggle-chip-active' : '')} onClick={() => setNewsletterOnly(true)}>
                                        <FontAwesomeIcon icon={faNewspaper} /> Newsletter{counts ? ' (' + counts.newsletter + ')' : ''}
                                    </button>
                                    <button type="button" role="radio" aria-checked={!newsletterOnly} className={'toggle-chip' + (!newsletterOnly ? ' toggle-chip-active' : '')} onClick={() => setNewsletterOnly(false)}>
                                        <FontAwesomeIcon icon={faCircleInfo} /> Information{counts ? ' (' + counts.all + ')' : ''}
                                    </button>
                                </div>
                                <small className='adminmail-field-hint'>
                                    {newsletterOnly
                                        ? 'Nur User mit Newsletter-Zustimmung. Geeignet für Neuigkeiten und Updates.'
                                        : 'Alle aktiven User. Nur für wichtige Service-Informationen (Wartung, AGB, Datenschutz).'}
                                </small>
                            </div>

                            {!newsletterOnly ? (
                                <div className='status-banner status-banner--error adminmail-warning' role="note">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>Information geht an alle aktiven User – nur für wichtige Service-Hinweise verwenden. Werbliche Inhalte und Neuigkeiten als Newsletter senden.</span>
                                </div>
                            ) : null}

                            <div className='field'>
                                <label>Kanal</label>
                                <div className='adminmail-chips' role="radiogroup" aria-label="Kanal">
                                    <button type="button" role="radio" aria-checked={!profileOnly} className={'toggle-chip' + (!profileOnly ? ' toggle-chip-active' : '')} onClick={() => { setProfileOnly(false); setConfirmed(false) }}>
                                        <FontAwesomeIcon icon={faEnvelopesBulk} /> E-Mail + Profil
                                    </button>
                                    <button type="button" role="radio" aria-checked={profileOnly} className={'toggle-chip' + (profileOnly ? ' toggle-chip-active' : '')} onClick={() => { setProfileOnly(true); setConfirmed(false) }}>
                                        <FontAwesomeIcon icon={faBell} /> Ohne E-Mail
                                    </button>
                                </div>
                                <small className='adminmail-field-hint'>
                                    {profileOnly
                                        ? 'Keine E-Mail – erscheint nur unter Profil → Benachrichtigungen.'
                                        : 'E-Mail-Versand; die Nachricht erscheint zusätzlich unter Benachrichtigungen.'}
                                </small>
                            </div>

                            <div className='field'>
                                <label htmlFor="am-subject">Betreff</label>
                                <input id="am-subject" type="text" maxLength="150" placeholder={profileOnly ? "Betreff der Benachrichtigung" : "Betreff der E-Mail"} value={subject} onInput={(e) => setSubject(e.target.value)}></input>
                            </div>

                            <div className='field'>
                                <label htmlFor="am-text">Nachricht</label>
                                <div className='adminmail-format-bar' role="toolbar" aria-label="Formatierung" aria-controls="am-text">
                                    <button type="button" className='adminmail-format-btn' title="Fett (⌘/Strg + B)" aria-label="Fett" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('**')}>
                                        <FontAwesomeIcon icon={faBold} />
                                    </button>
                                    <button type="button" className='adminmail-format-btn' title="Kursiv (⌘/Strg + I)" aria-label="Kursiv" onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('*')}>
                                        <FontAwesomeIcon icon={faItalic} />
                                    </button>
                                    <span className='adminmail-format-hint'>**fett** · *kursiv* – Text markieren und Button klicken</span>
                                </div>
                                <textarea
                                    id="am-text"
                                    ref={messageRef}
                                    rows="10"
                                    maxLength={MESSAGE_MAX_LENGTH}
                                    placeholder={profileOnly
                                        ? "Nachrichtentext – Absätze werden übernommen. Die Anrede wird im Profil automatisch ergänzt."
                                        : "Nachrichtentext – Absätze werden übernommen. Anrede und Helferzeit-Footer werden in der E-Mail automatisch ergänzt."}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={onMessageKeyDown}
                                />
                            </div>

                            {message.trim() ? (
                                <div className='adminmail-preview'>
                                    <span className='adminmail-preview-label'><FontAwesomeIcon icon={faEye} /> Vorschau</span>
                                    <p className='adminmail-preview-text'>
                                        <span className='adminmail-preview-greeting'>{greetingPreview(cur_firstname, cur_lastname)}</span>
                                        {'\n\n'}
                                        {renderFormattedText(message)}
                                    </p>
                                </div>
                            ) : null}

                            <label className="chb">
                                <input type="checkbox" name="testonlychb" checked={testOnly} onChange={() => { setTestOnly(!testOnly); setConfirmed(false) }}></input>
                                <span className="chb-mark"></span>
                                <span>
                                    <FontAwesomeIcon icon={faFlask} />
                                    {' '}
                                    {profileOnly ? 'Nur Test an mich (Profil)' : 'Nur Testmail an mich senden'}
                                </span>
                            </label>
                            {!testOnly ? (
                                <label className="chb">
                                    <input type="checkbox" name="confirmchb" checked={confirmed} onChange={() => setConfirmed(!confirmed)}></input>
                                    <span className="chb-mark"></span>
                                    <span style={{ fontWeight: 700, color: 'var(--warn)' }}>
                                        {profileOnly
                                            ? ('Veröffentlichung für ' + (recipientCount != null ? recipientCount : '…') + ' Empfänger bestätigen')
                                            : ('Senden an ' + (recipientCount != null ? recipientCount : '…') + ' Empfänger bestätigen')}
                                    </span>
                                </label>
                            ) : null}

                            {banner ? (
                                <div className={'adminmail-banner status-banner status-banner--' + (banner.type === 'error' ? 'error' : banner.type === 'done' ? 'success' : 'info')} role={banner.type === 'error' ? 'alert' : 'status'}>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                    <span>
                                        {banner.text}
                                        {banner.runid ? <><br /><small>Für Debugging: RunId={banner.runid}</small></> : null}
                                    </span>
                                </div>
                            ) : null}

                            <div style={{ marginTop: '18px' }}>
                                <CTAButton variant="primary" hideArrow={false} title={submitLabel()} onclick={() => sendMails()} />
                            </div>
                        </div>
                    </motion.div>

                    <motion.div variants={boxvar2_2} initial="hidden" whileInView="visible" viewport={{ once: false, margin: "-50px" }} style={{ marginTop: '24px' }}>
                        <div className='card adminmail-card'>
                            <div className='adminmail-headline'>
                                <span className='adminmail-icon'><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                                <h3>Letzte Plattform-Rundmails</h3>
                            </div>

                            {!loadedHistory ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <Skeleton height="64px" />
                                    <Skeleton height="64px" />
                                </div>
                            ) : history.length === 0 ? (
                                <p className='adminmail-empty'>Noch keine Plattform-Rundmails versendet.</p>
                            ) : (
                                <ul className='adminmail-history'>
                                    {history.map(function (entry) {
                                        const isOpen = expandedId === entry._id
                                        return (
                                            <li key={entry._id} className={'adminmail-history-item' + (isOpen ? ' is-open' : '')}>
                                                <div
                                                    className='adminmail-history-summary'
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-expanded={isOpen ? 'true' : 'false'}
                                                    onClick={() => setExpandedId(isOpen ? null : entry._id)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedId(isOpen ? null : entry._id) } }}
                                                >
                                                    <div className='adminmail-history-top'>
                                                        <span className='adminmail-history-meta'>
                                                            {entry.sentAt ? formatDateWithTime(new Date(entry.sentAt)) : '—'}
                                                            {' · von '}
                                                            {entry.sender ? entry.sender.firstname + ' ' + entry.sender.lastname : 'Unbekannt'}
                                                        </span>
                                                        <span className='adminmail-history-badges'>
                                                            {entry.testOnly ? <span className='badge badge-accent'><FontAwesomeIcon icon={faFlask} /> Test</span> : null}
                                                            {entry.profileOnly
                                                                ? <span className='badge badge-accent badge-icon' title="Benachrichtigung – nur im Profil (keine E-Mail)" aria-label="Benachrichtigung – nur im Profil (keine E-Mail)"><FontAwesomeIcon icon={faBell} /></span>
                                                                : <span className='badge badge-brand badge-icon' title="Per E-Mail versendet und im Profil gespeichert" aria-label="Per E-Mail versendet und im Profil gespeichert"><FontAwesomeIcon icon={faEnvelope} /></span>}
                                                            {entry.newsletterOnly !== false
                                                                ? <span className='badge badge-info'><FontAwesomeIcon icon={faNewspaper} /> Newsletter</span>
                                                                : <span className='badge badge-brand'><FontAwesomeIcon icon={faCircleInfo} /> Information</span>}
                                                            <span className='badge badge-info' title="Empfänger"><FontAwesomeIcon icon={faUsers} /> {entry.recipientCount}</span>
                                                        </span>
                                                    </div>
                                                    <b className='adminmail-history-subject'>{entry.subject}</b>
                                                    <small className='adminmail-history-runid'>RunId={entry.runid}</small>
                                                </div>
                                                {isOpen ? <p className='adminmail-history-text'>{renderFormattedText(entry.messageText)}</p> : null}
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                        </div>
                    </motion.div>
                </AdminGuard>
            </main>

            <MainFooter useEventStyle={false} />
        </div>
    )
}
